import React from 'react';
import { FiLoader, FiRefreshCw } from 'react-icons/fi';

interface LoaderProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullScreen?: boolean;
  message?: string;
  variant?: 'spinner' | 'dots' | 'pulse';
  className?: string;
}

export const Loader: React.FC<LoaderProps> = ({ 
  size = 'md', 
  fullScreen = false, 
  message,
  variant = 'spinner',
  className = ''
}) => {
  const sizeClasses = {
    xs: 'h-4 w-4',
    sm: 'h-6 w-6',
    md: 'h-12 w-12',
    lg: 'h-16 w-16',
    xl: 'h-24 w-24',
  };

  const renderLoader = () => {
    switch (variant) {
      case 'dots':
        return (
          <div className="flex gap-1">
            <div className={`${sizeClasses[size] === 'h-4 w-4' ? 'w-2 h-2' : 'w-3 h-3'} bg-primary rounded-full animate-bounce`} style={{ animationDelay: '0ms' }}></div>
            <div className={`${sizeClasses[size] === 'h-4 w-4' ? 'w-2 h-2' : 'w-3 h-3'} bg-primary rounded-full animate-bounce`} style={{ animationDelay: '150ms' }}></div>
            <div className={`${sizeClasses[size] === 'h-4 w-4' ? 'w-2 h-2' : 'w-3 h-3'} bg-primary rounded-full animate-bounce`} style={{ animationDelay: '300ms' }}></div>
          </div>
        );
      case 'pulse':
        return (
          <div className={`${sizeClasses[size]} bg-primary/30 rounded-full animate-pulse`}></div>
        );
      default:
        return (
          <div className={`animate-spin rounded-full border-t-2 border-b-2 border-primary ${sizeClasses[size]} ${className}`}></div>
        );
    }
  };

  const loader = (
    <div className="flex flex-col items-center justify-center gap-3">
      {renderLoader()}
      {message && <p className="text-muted text-sm font-medium animate-pulse">{message}</p>}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        {loader}
      </div>
    );
  }

  return loader;
};

// Button loading spinner - inline with text
interface ButtonSpinnerProps {
  size?: 'xs' | 'sm' | 'md';
  className?: string;
}

export const ButtonSpinner: React.FC<ButtonSpinnerProps> = ({ size = 'sm', className = '' }) => {
  const sizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
  };

  return (
    <div className={`animate-spin rounded-full border-t-2 border-b-2 border-current ${sizeClasses[size]} ${className}`}></div>
  );
};

// Skeleton loaders for different content types
interface SkeletonProps {
  className?: string;
  animate?: boolean;
}

export const SkeletonText: React.FC<SkeletonProps & { lines?: number }> = ({ 
  className = '', 
  lines = 1,
  animate = true 
}) => {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <div 
          key={i} 
          className={`h-4 bg-muted/20 rounded ${animate ? 'animate-pulse' : ''}`}
          style={{ width: i === lines - 1 && lines > 1 ? '75%' : '100%' }}
        ></div>
      ))}
    </div>
  );
};

export const SkeletonCard: React.FC<SkeletonProps> = ({ className = '', animate = true }) => {
  return (
    <div className={`rounded-xl border border-border bg-surface p-6 ${className}`}>
      <div className="flex items-center gap-4 mb-4">
        <div className={`w-12 h-12 rounded-full bg-muted/20 ${animate ? 'animate-pulse' : ''}`}></div>
        <div className="flex-1 space-y-2">
          <div className={`h-4 bg-muted/20 rounded w-1/3 ${animate ? 'animate-pulse' : ''}`}></div>
          <div className={`h-3 bg-muted/20 rounded w-1/2 ${animate ? 'animate-pulse' : ''}`}></div>
        </div>
      </div>
      <div className="space-y-3">
        <div className={`h-3 bg-muted/20 rounded ${animate ? 'animate-pulse' : ''}`}></div>
        <div className={`h-3 bg-muted/20 rounded w-5/6 ${animate ? 'animate-pulse' : ''}`}></div>
        <div className={`h-3 bg-muted/20 rounded w-4/6 ${animate ? 'animate-pulse' : ''}`}></div>
      </div>
    </div>
  );
};

export const SkeletonTable: React.FC<SkeletonProps & { rows?: number; cols?: number }> = ({ 
  className = '', 
  rows = 5,
  cols = 4,
  animate = true 
}) => {
  return (
    <div className={`rounded-xl border border-border bg-surface overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex border-b border-border bg-surface-elevated p-4 gap-4">
        {Array.from({ length: cols }).map((_, i) => (
          <div key={i} className={`h-4 bg-muted/20 rounded flex-1 ${animate ? 'animate-pulse' : ''}`}></div>
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex border-b border-border last:border-b-0 p-4 gap-4">
          {Array.from({ length: cols }).map((_, colIndex) => (
            <div 
              key={colIndex} 
              className={`h-4 bg-muted/20 rounded flex-1 ${animate ? 'animate-pulse' : ''}`}
              style={{ animationDelay: `${(rowIndex * cols + colIndex) * 50}ms` }}
            ></div>
          ))}
        </div>
      ))}
    </div>
  );
};

export const SkeletonStats: React.FC<SkeletonProps & { count?: number }> = ({ 
  className = '', 
  count = 3,
  animate = true 
}) => {
  return (
    <div className={`grid grid-cols-1 md:grid-cols-${count} gap-4 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl border border-border bg-surface p-6">
          <div className={`h-3 bg-muted/20 rounded w-1/2 mb-3 ${animate ? 'animate-pulse' : ''}`}></div>
          <div className={`h-8 bg-muted/20 rounded w-3/4 mb-2 ${animate ? 'animate-pulse' : ''}`}></div>
          <div className={`h-3 bg-muted/20 rounded w-2/3 ${animate ? 'animate-pulse' : ''}`}></div>
        </div>
      ))}
    </div>
  );
};

// Loading overlay for sections
interface LoadingOverlayProps {
  isLoading: boolean;
  message?: string;
  children: React.ReactNode;
  blur?: boolean;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ 
  isLoading, 
  message, 
  children,
  blur = true 
}) => {
  return (
    <div className="relative">
      {children}
      {isLoading && (
        <div className={`absolute inset-0 bg-background/80 ${blur ? 'backdrop-blur-sm' : ''} flex items-center justify-center z-10 rounded-xl`}>
          <Loader message={message} />
        </div>
      )}
    </div>
  );
};

// Action button with built-in loading state
interface LoadingButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isLoading?: boolean;
  loadingText?: string;
  icon?: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
}

export const LoadingButton: React.FC<LoadingButtonProps> = ({
  isLoading = false,
  loadingText,
  icon,
  variant = 'primary',
  children,
  disabled,
  className = '',
  ...props
}) => {
  const variantClasses = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    danger: 'btn-danger',
    ghost: 'btn-ghost',
  };

  return (
    <button
      disabled={disabled || isLoading}
      className={`${variantClasses[variant]} flex items-center justify-center gap-2 ${className} ${isLoading ? 'opacity-80' : ''}`}
      {...props}
    >
      {isLoading ? (
        <>
          <ButtonSpinner />
          <span>{loadingText || children}</span>
        </>
      ) : (
        <>
          {icon}
          {children}
        </>
      )}
    </button>
  );
};

// Inline loading indicator for subtle operations
interface InlineLoaderProps {
  isLoading: boolean;
  size?: 'xs' | 'sm';
}

export const InlineLoader: React.FC<InlineLoaderProps> = ({ isLoading, size = 'xs' }) => {
  if (!isLoading) return null;
  
  return (
    <FiRefreshCw className={`${size === 'xs' ? 'h-3 w-3' : 'h-4 w-4'} animate-spin text-muted`} />
  );
};

export default Loader;
